import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { orgchartAPI } from '../lib/api';
import { useOrgChartLayout } from '../components/orgchart/useOrgChartLayout';
import { OrgChartCanvas } from '../components/orgchart/OrgChartCanvas';
import { OrgChartSidePanel } from '../components/orgchart/OrgChartSidePanel';
import { OrgChartToolbar } from '../components/orgchart/OrgChartToolbar';
import { OrgChartEditModal, OrgChartDeleteConfirm } from '../components/orgchart/OrgChartEditModal';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

export default function OrgChart() {
  const { user } = useAuth();
  const { canWrite: canWritePerm, isAdmin } = usePermissions();
  // Effective role for toolbar write gate: 'admin' (full CRUD) vs other
  // A user/viewer with 'full' on organigrama gets admin-level toolbar
  const canWriteOrg = isAdmin || canWritePerm('organigrama');
  const userRole = canWriteOrg ? 'admin' : (user?.role || 'user');

  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [editMode, setEditMode] = useState('edit');

  // Delete confirm state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingNode, setDeletingNode] = useState(null);

  const { positions, connections, levelLines, bounds, visibleNodeIds } = useOrgChartLayout(nodes, activeFilter);

  const loadNodes = useCallback(async () => {
    try {
      const res = await orgchartAPI.getAll();
      setNodes(res.data.nodes || []);
    } catch (err) {
      toast.error('Error al cargar organigrama');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNodes(); }, [loadNodes]);

  const handleSelectNode = useCallback((nodeId) => {
    setSelectedNodeId(prev => prev === nodeId ? null : nodeId);
  }, []);

  const handleDeselectNode = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handleSearch = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
  }, []);

  const handleEditNode = useCallback((node) => {
    setEditingNode(node);
    setEditMode('edit');
    setEditModalOpen(true);
  }, []);

  const handleAddNode = useCallback(() => {
    setEditingNode(null);
    setEditMode('create');
    setEditModalOpen(true);
  }, []);

  const handleDeleteNode = useCallback((node) => {
    setDeletingNode(node);
    setDeleteConfirmOpen(true);
  }, []);

  const handleSaveNode = useCallback(async (data) => {
    if (editMode === 'create') {
      const res = await orgchartAPI.create(data);
      toast.success('Posicion creada');
    } else if (editingNode) {
      await orgchartAPI.update(editingNode.node_id, data);
      toast.success('Posicion actualizada');
    }
    await loadNodes();
  }, [editMode, editingNode, loadNodes]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingNode) return;
    await orgchartAPI.delete(deletingNode.node_id);
    toast.success('Posicion eliminada');
    if (selectedNodeId === deletingNode.node_id) setSelectedNodeId(null);
    await loadNodes();
  }, [deletingNode, selectedNodeId, loadNodes]);

  const handleNavigateFromPanel = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
  }, []);

  const selectedNode = nodes.find(n => n.node_id === selectedNodeId);
  const deletingChildCount = deletingNode ? nodes.filter(n => n.parent_id === deletingNode.node_id).length : 0;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4" data-testid="orgchart-loading">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[600px] w-full rounded-xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-100px)]" data-testid="orgchart-page">
        {/* Header */}
        <div className="mb-3">
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Organigrama</h1>
          <p className="text-slate-500 text-sm mt-0.5">Estructura organizacional de Central Aretina</p>
        </div>

        {/* Toolbar */}
        <OrgChartToolbar
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          onSearch={handleSearch}
          isEditMode={isEditMode}
          onToggleEdit={() => setIsEditMode(!isEditMode)}
          onAddNode={handleAddNode}
          userRole={userRole}
          nodes={nodes}
        />

        {/* Canvas */}
        <div className="flex-1 mt-2 rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
          <OrgChartCanvas
            nodes={nodes}
            positions={positions}
            connections={connections}
            levelLines={levelLines}
            bounds={bounds}
            visibleNodeIds={visibleNodeIds}
            selectedNodeId={selectedNodeId}
            onSelectNode={handleSelectNode}
            onDeselectNode={handleDeselectNode}
            activeFilter={activeFilter}
            isEditMode={isEditMode}
            onEditNode={handleEditNode}
            onDeleteNode={handleDeleteNode}
          />
        </div>
      </div>

      {/* Side Panel — no overlay, panel stays open during pan */}
      {selectedNode && !isEditMode && (
        <OrgChartSidePanel
          node={selectedNode}
          allNodes={nodes}
          onClose={handleDeselectNode}
          onNavigate={handleNavigateFromPanel}
          userRole={userRole}
        />
      )}

      {/* Edit Modal */}
      <OrgChartEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        node={editingNode}
        allNodes={nodes}
        onSave={handleSaveNode}
        mode={editMode}
      />

      {/* Delete Confirmation */}
      <OrgChartDeleteConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        node={deletingNode}
        childrenCount={deletingChildCount}
        onConfirm={handleConfirmDelete}
      />
    </Layout>
  );
}
